import { useMemo } from 'react';
import { useGetRouterContextQuery } from '../../../api/queries/getRouterContext';
import { useRuntimeConfig } from '../../../runtimeConfig';
import getRoutesFromRouter from '../../../utilities/getRoutesFromRouter';
import LoadingElement from '../../LoadingElement';
import NotFoundResult from '../../results/NotFoundResult';
import { CompanyContextManager } from '../CompanyContextManager';
import { LanguageContextManager } from '../LanguageContextManager';
import { RouterContext, RouterRoutesContext } from './Context';

export type RouterContextManagerProps = { children: JSX.Element | React.ReactNode };

export const RouterContextManager = ({ children }: RouterContextManagerProps) => {
    const { router: runtime } = useRuntimeConfig();

    const { data, loading, refetch } = useGetRouterContextQuery({
        fetchPolicy: 'no-cache', // we want to get the data to always be up to date
        variables: runtime.id ? { routerId: runtime.id } : undefined, // we may have no router ID to fetch
        skip: !runtime.id, // skip this if there's indeed no router to fetch
    });

    const routerData = data?.router;

    const routes = useMemo(() => (routerData ? getRoutesFromRouter(routerData) : []), [routerData]);

    const router = useMemo(() => {
        if (routerData) {
            return { ...routerData, refetch: () => refetch() };
        }

        return null;
    }, [routerData, refetch]);

    if (runtime.id && loading) {
        // we should wait for data to be here
        // if there's a router there may be load in our application routing
        return <LoadingElement />;
    }

    if (runtime.id && !router) {
        // that shouldn't ever happen
        return <NotFoundResult />;
    }

    // finally initialize the router
    // all router have companies so we can provide the company we receive from here
    return (
        <RouterContext.Provider value={router}>
            <RouterRoutesContext.Provider value={routes}>
                <LanguageContextManager languages={router?.languages}>
                    <CompanyContextManager enforcedCompany={router?.company}>{children}</CompanyContextManager>
                </LanguageContextManager>
            </RouterRoutesContext.Provider>
        </RouterContext.Provider>
    );
};

export default RouterContextManager;
